import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  createSupplyRequest,
  getSupplyRequests,
  getAdminSupplyRequests,
  runSupplyRequestPullSync,
  updateSupplyRequest,
  addSupplyRequestLocalNote,
  adminBulkUpdateSupplyRequestsStatus,
  adminBulkMoveSupplyRequests,
  adminBulkDeleteSupplyRequestsLocal,
  adminCleanupMirroredSupplyRequestTasks,
} from '../services/supplyRequestService';
import { useSpaces } from '../context/SpaceContext';
import { useAuth } from '../context/AuthContext';
import { deleteFile, downloadFile, openFile, uploadFile } from '../services/fileService';
import './WrikeSupplyRequestsPage.css';

const TAB_KEYS = ['newRequests', 'onOrder', 'recentlyCompleted', 'all'];

const TAB_LABELS = {
  newRequests: 'New',
  onOrder: 'On Order',
  recentlyCompleted: 'Recently Completed',
  all: 'All',
};

const EMPTY_FORM = {
  itemsNeeded: '',
  department: '',
  eventName: '',
  requiredByDate: '',
  websiteLink: '',
  totalCostIncludingHst: '',
  quoteNotes: '',
  notes: '',
  localNoteText: '',
};

const joinPeople = (people) => {
  if (!Array.isArray(people) || people.length === 0) return 'Unassigned';
  return people.map((person) => person.name || person.email || person.id).join(', ');
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const parseCurrencyValue = (value) => {
  const numeric = Number(String(value || '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(numeric) ? numeric : 0;
};

const formatBytes = (bytes) => {
  const size = Number(bytes || 0);
  if (!Number.isFinite(size) || size <= 0) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const shouldDownloadOnOpen = (file) => {
  const name = String(file?.name || '');
  const officeExtension = /\.(doc|docx|xls|xlsx|ppt|pptx)$/i;
  return officeExtension.test(name);
};

const toHttpUrl = (value) => {
  const text = String(value || '').trim();
  if (!text) return '';
  if (/^https?:\/\//i.test(text)) return text;
  if (/^[\w.-]+\.[a-z]{2,}(\/.*)?$/i.test(text)) return `https://${text}`;
  return '';
};

const urlDisplayLabel = (value) => {
  const href = toHttpUrl(value);
  if (!href) return String(value || '').trim();
  try {
    const parsed = new URL(href);
    return parsed.hostname.replace(/^www\./i, '') || href;
  } catch {
    return href;
  }
};

const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'on-order', label: 'On Order' },
  { value: 'completed', label: 'Completed' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'other', label: 'Other / Custom' },
];

export default function WrikeSupplyRequestsPage() {
  const { user } = useAuth();
  const { spaces, activeSpace } = useSpaces();
  const [searchParams] = useSearchParams();
  const requestedSpaceId = searchParams.get('spaceId') || '';
  const isAdmin = user?.role === 'admin';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('newRequests');

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formFiles, setFormFiles] = useState([]);
  const [createSpaceId, setCreateSpaceId] = useState('');
  const [filterSpaceId, setFilterSpaceId] = useState(requestedSpaceId);
  const [pushToWrike, setPushToWrike] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [cleaningLegacyTasks, setCleaningLegacyTasks] = useState(false);
  const [statusSavingId, setStatusSavingId] = useState('');
  const [statusDraft, setStatusDraft] = useState({});
  const [customStatusDraft, setCustomStatusDraft] = useState({});
  const [editingId, setEditingId] = useState('');
  const [editDraft, setEditDraft] = useState(null);
  const [editSavingId, setEditSavingId] = useState('');
  const [pushSavingId, setPushSavingId] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [statusFilter, setStatusFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [requiredByFilter, setRequiredByFilter] = useState('');
  const [attachmentFilter, setAttachmentFilter] = useState('');
  const [syncStateFilter, setSyncStateFilter] = useState('');
  const [search, setSearch] = useState('');
  const [adminViewAll, setAdminViewAll] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkStatus, setBulkStatus] = useState('new');
  const [bulkMoveSpaceId, setBulkMoveSpaceId] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);
  const [deletingId, setDeletingId] = useState('');
  const [bulkFeedback, setBulkFeedback] = useState(null);
  const [localNoteDrafts, setLocalNoteDrafts] = useState({});
  const [localNoteSavingId, setLocalNoteSavingId] = useState('');

  const resetFormFiles = () => setFormFiles([]);

  const spaceById = useMemo(() => new Map(spaces.map((space) => [String(space._id), space])), [spaces]);

  useEffect(() => {
    if (!createSpaceId && spaces.length > 0) {
      const preferredSpaceId = requestedSpaceId || activeSpace?._id || '';
      const preferredSpace = spaces.find((space) => String(space._id) === String(preferredSpaceId));
      setCreateSpaceId(preferredSpace?._id || spaces[0]._id);
    }
  }, [activeSpace?._id, createSpaceId, requestedSpaceId, spaces]);

  useEffect(() => {
    if (requestedSpaceId && spaceById.has(String(requestedSpaceId))) {
      setFilterSpaceId(requestedSpaceId);
    }
  }, [requestedSpaceId, spaceById]);

  useEffect(() => {
    const selectedSpace = spaceById.get(String(createSpaceId));
    setPushToWrike(Boolean(selectedSpace?.settings?.allowSupplyRequestWrikePush));
  }, [createSpaceId, spaceById]);

  const loadRequests = useCallback(async ({ isRefresh = false } = {}) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const params = filterSpaceId ? { spaceId: filterSpaceId } : {};
      if (syncStateFilter === 'orphaned_from_wrike') {
        params.includeOrphaned = true;
      }
      const res = isAdmin && adminViewAll
        ? await getAdminSupplyRequests(params)
        : await getSupplyRequests(params);
      setData(res.data);
      setError(null);
    } catch (err) {
      const apiError = err?.response?.data;
      const hasDiagnostics =
        apiError &&
        typeof apiError === 'object' &&
        Object.prototype.hasOwnProperty.call(apiError, 'status') &&
        Object.prototype.hasOwnProperty.call(apiError, 'message') &&
        Object.prototype.hasOwnProperty.call(apiError, 'wrikeErrorBody');

      if (hasDiagnostics) {
        setError({
          message: apiError.message || 'Failed to load Wrike supply requests.',
          diagnostics: {
            status: apiError.status,
            message: apiError.message,
            attemptedHosts: apiError.attemptedHosts || [],
            redirectLocation: apiError.redirectLocation || '',
            wrikeErrorBody: apiError.wrikeErrorBody,
          },
        });
      } else {
        setError({
          message: apiError?.message || 'Failed to load Wrike supply requests.',
          diagnostics: null,
        });
      }
    } finally {
      if (isRefresh) setRefreshing(false);
      else setLoading(false);
    }
  }, [filterSpaceId, isAdmin, adminViewAll, syncStateFilter]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  useEffect(() => {
    setSelectedIds([]);
  }, [activeTab, filterSpaceId, statusFilter, departmentFilter, requiredByFilter, attachmentFilter, syncStateFilter, search, adminViewAll]);

  useEffect(() => {
    // Sync-state filtering is cross-status by intent; default to All so users can see matching items immediately.
    if (syncStateFilter && activeTab !== 'all') {
      setActiveTab('all');
    }
  }, [syncStateFilter, activeTab]);

  const availableStatuses = data?.availableStatuses || [];
  const availableDepartments = data?.availableDepartments || [];

  const filterTasksByControls = useCallback((tasks = []) => {
    const query = search.trim().toLowerCase();

    return (Array.isArray(tasks) ? tasks : []).filter((task) => {
      if (filterSpaceId && String(task.spaceId || '') !== filterSpaceId) return false;
      if (statusFilter && task.visibleStatus !== statusFilter) return false;
      if (departmentFilter && task.parsedFields?.department !== departmentFilter) return false;
      if (attachmentFilter === 'with' && !task.hasAttachments) return false;
      if (attachmentFilter === 'without' && task.hasAttachments) return false;
      if (syncStateFilter && task.syncState !== syncStateFilter) return false;
      if (requiredByFilter && String(task.parsedFields?.requiredByDate || task.dueDate || '').slice(0, 10) !== requiredByFilter) return false;

      if (!query) return true;

      const haystack = [
        task.title,
        task.descriptionPreview,
        task.parsedFields?.itemsNeeded,
        task.parsedFields?.department,
        task.parsedFields?.eventName,
        ...(Array.isArray(task.wrikeComments) ? task.wrikeComments.map((comment) => comment.text) : []),
        ...(Array.isArray(task.localNotes) ? task.localNotes.map((note) => note.text) : []),
        ...(Array.isArray(task.importedFiles) ? task.importedFiles.map((file) => file.name) : []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [attachmentFilter, departmentFilter, filterSpaceId, requiredByFilter, search, statusFilter, syncStateFilter]);

  const filteredTasks = useMemo(() => {
    const tasks = data?.[activeTab] || data?.groups?.[activeTab] || [];
    return filterTasksByControls(tasks);
  }, [activeTab, data, filterTasksByControls]);

  const filteredCounts = useMemo(() => {
    const next = {};
    for (const key of TAB_KEYS) {
      const tasks = data?.[key] || data?.groups?.[key] || [];
      next[key] = filterTasksByControls(tasks).length;
    }
    return next;
  }, [data, filterTasksByControls]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    resetFormFiles();
    setFormError('');
  };

  const handleFormFilesChange = (e) => {
    const selected = Array.from(e.target.files || []);
    if (selected.length === 0) return;

    setFormFiles((prev) => [...prev, ...selected]);
    e.target.value = '';
  };

  const removeFormFile = (index) => {
    setFormFiles((prev) => prev.filter((_file, fileIndex) => fileIndex !== index));
  };

  const handleCreate = async (e) => {
    e.preventDefault();

    if (!form.itemsNeeded.trim()) {
      setFormError('What item(s) is needed? is required.');
      return;
    }

    if (form.websiteLink && !/^https?:\/\//i.test(form.websiteLink)) {
      setFormError('Website link must start with http:// or https://');
      return;
    }

    if (parseCurrencyValue(form.totalCostIncludingHst) > 1000 && !form.quoteNotes.trim()) {
      setFormError('Quote notes / 3 quotes are required for requests over $1000.');
      return;
    }

    setSaving(true);
    setFormError('');

    try {
      const uploadedFiles = [];
      try {
        for (const file of formFiles) {
          const response = await uploadFile(createSpaceId || null, file);
          uploadedFiles.push(response.data.file);
        }
      } catch (uploadErr) {
        await Promise.allSettled(uploadedFiles.map((file) => deleteFile(file._id)));
        throw uploadErr;
      }

      await createSupplyRequest({
        ...form,
        title: form.itemsNeeded.trim(),
        spaceId: createSpaceId || undefined,
        pushToWrike,
        attachmentFileIds: uploadedFiles.map((file) => file._id),
        localNoteText: form.localNoteText,
      });
      setShowForm(false);
      resetForm();
      setActiveTab('newRequests');
      await loadRequests({ isRefresh: true });
    } catch (err) {
      setFormError(err?.response?.data?.message || 'Failed to create supply request.');
    } finally {
      setSaving(false);
    }
  };

  const handlePullSync = async () => {
    setSyncing(true);
    try {
      await runSupplyRequestPullSync();
      await loadRequests({ isRefresh: true });
    } catch {
      // List endpoint will surface any current errors on refresh.
    } finally {
      setSyncing(false);
    }
  };

  const handleAdminCleanupMirroredTasks = async () => {
    if (!window.confirm('Clean up legacy Wrike-mirrored board tasks now? This removes old task-board copies and keeps records in Supply Requests only.')) {
      return;
    }

    setCleaningLegacyTasks(true);
    try {
      const { data: cleanup } = await adminCleanupMirroredSupplyRequestTasks();
      const deletedTasks = Number(cleanup?.deletedTasks || 0);
      const clearedLinks = Number(cleanup?.clearedLocalTaskLinks || 0);
      alert(`Cleanup complete. Deleted ${deletedTasks} mirrored task(s) and cleared ${clearedLinks} supply-request link(s).`);
      await loadRequests({ isRefresh: true });
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to clean up mirrored tasks.');
    } finally {
      setCleaningLegacyTasks(false);
    }
  };

  const handleStatusSave = async (task, nextStatus) => {
    setStatusSavingId(task.id);
    try {
      const payload = { status: nextStatus };
      const taskSpace = spaceById.get(String(task.spaceId));
      const canPushStatusToWrike = Boolean(task.wrikeTaskId && taskSpace?.settings?.allowSupplyRequestWrikePush);
      if (nextStatus === 'other') {
        const customLabel = String(customStatusDraft[task.id] || task.visibleStatus || '').trim();
        if (!customLabel) {
          return;
        }
        payload.visibleStatusLabel = customLabel;
      }

      if (canPushStatusToWrike) {
        payload.pushToWrikeNow = true;
      }

      await updateSupplyRequest(task.id, payload);
      setStatusDraft((prev) => {
        const next = { ...prev };
        delete next[task.id];
        return next;
      });
      await loadRequests({ isRefresh: true });
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to update status.');
      await loadRequests({ isRefresh: true });
    } finally {
      setStatusSavingId('');
    }
  };

  const handleStatusSelect = (task, nextStatus) => {
    setStatusDraft((prev) => ({
      ...prev,
      [task.id]: nextStatus,
    }));

    if (nextStatus === 'other') {
      setCustomStatusDraft((prev) => ({
        ...prev,
        [task.id]: prev[task.id] ?? (task.standardStatus === 'other' ? task.visibleStatus : ''),
      }));
      return;
    }

    handleStatusSave(task, nextStatus);
  };

  const handleCustomStatusSave = (task) => {
    handleStatusSave(task, 'other');
  };

  const startEdit = (task) => {
    setEditingId(task.id);
    setEditDraft({
      itemsNeeded: task.parsedFields?.itemsNeeded || task.title || '',
      department: task.parsedFields?.department || '',
      eventName: task.parsedFields?.eventName || '',
      requiredByDate: String(task.parsedFields?.requiredByDate || task.dueDate || '').slice(0, 10),
      websiteLink: task.parsedFields?.websiteLink || '',
      totalCostIncludingHst: task.parsedFields?.totalCostIncludingHst || '',
      quoteNotes: task.parsedFields?.quoteNotes || '',
      notes: task.descriptionPreview || '',
      spaceId: task.spaceId || createSpaceId || '',
    });
  };

  const cancelEdit = () => {
    setEditingId('');
    setEditDraft(null);
  };

  const handleEditField = (field, value) => {
    setEditDraft((prev) => ({ ...(prev || {}), [field]: value }));
  };

  const saveLocalEdits = async (task, { pushAfterSave = false } = {}) => {
    if (!editDraft) return;
    if (!String(editDraft.itemsNeeded || '').trim()) return;
    if (editDraft.websiteLink && !/^https?:\/\//i.test(editDraft.websiteLink)) return;
    if (parseCurrencyValue(editDraft.totalCostIncludingHst) > 1000 && !String(editDraft.quoteNotes || '').trim()) return;

    if (pushAfterSave) setPushSavingId(task.id);
    else setEditSavingId(task.id);
    try {
      await updateSupplyRequest(task.id, {
        title: editDraft.itemsNeeded,
        itemsNeeded: editDraft.itemsNeeded,
        department: editDraft.department,
        eventName: editDraft.eventName,
        requiredByDate: editDraft.requiredByDate,
        websiteLink: editDraft.websiteLink,
        totalCostIncludingHst: editDraft.totalCostIncludingHst,
        quoteNotes: editDraft.quoteNotes,
        notes: editDraft.notes,
        spaceId: editDraft.spaceId || undefined,
        pushToWrikeNow: pushAfterSave,
      });
      cancelEdit();
      await loadRequests({ isRefresh: true });
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to save local edits.');
      await loadRequests({ isRefresh: true });
    } finally {
      setEditSavingId('');
      setPushSavingId('');
    }
  };

  const pushLocalToWrike = async (task) => {
    setPushSavingId(task.id);
    try {
      await updateSupplyRequest(task.id, { pushToWrikeNow: true });
      await loadRequests({ isRefresh: true });
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to push updates to Wrike.');
      await loadRequests({ isRefresh: true });
    } finally {
      setPushSavingId('');
    }
  };

  const handleDeleteLocalCopy = async (task) => {
    if (!isAdmin) return;
    const label = task?.title || 'this request';
    if (!window.confirm(`Delete local copy for "${label}"? This does not delete the Wrike task.`)) {
      return;
    }

    setDeletingId(task.id);
    try {
      await adminBulkDeleteSupplyRequestsLocal([task.id]);
      await loadRequests({ isRefresh: true });
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to delete local copy.');
    } finally {
      setDeletingId('');
    }
  };

  const handleOpenImportedFile = async (file) => {
    try {
      if (shouldDownloadOnOpen(file)) {
        await downloadFile(file.id, file.name || 'download');
      } else {
        await openFile(file.id);
      }
    } catch (err) {
      alert(err?.message || 'Failed to open imported file.');
    }
  };

  const handleDownloadImportedFile = async (file) => {
    try {
      await downloadFile(file.id, file.name || 'download');
    } catch (err) {
      alert(err?.message || 'Failed to download imported file.');
    }
  };

  const handleAddLocalNote = async (task) => {
    const noteText = String(localNoteDrafts[task.id] || '').trim();
    if (!noteText) return;

    setLocalNoteSavingId(task.id);
    try {
      const { data } = await addSupplyRequestLocalNote(task.id, { text: noteText });
      setLocalNoteDrafts((prev) => ({ ...prev, [task.id]: '' }));
      if (data?.supplyRequest) {
        setData((prev) => {
          if (!prev) return prev;
          const nextTask = data.supplyRequest;
          const replaceTask = (taskList = []) => taskList.map((item) => (String(item.id) === String(task.id) ? nextTask : item));

          return {
            ...prev,
            newRequests: replaceTask(prev.newRequests),
            onOrder: replaceTask(prev.onOrder),
            recentlyCompleted: replaceTask(prev.recentlyCompleted),
            all: replaceTask(prev.all),
            groups: {
              ...(prev.groups || {}),
              newRequests: replaceTask(prev.groups?.newRequests || prev.newRequests || []),
              onOrder: replaceTask(prev.groups?.onOrder || prev.onOrder || []),
              recentlyCompleted: replaceTask(prev.groups?.recentlyCompleted || prev.recentlyCompleted || []),
              all: replaceTask(prev.groups?.all || prev.all || []),
            },
          };
        });
      }
      await loadRequests({ isRefresh: true });
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to add local note.');
    } finally {
      setLocalNoteSavingId('');
    }
  };

  const syncUi = (syncState) => {
    if (syncState === 'local_changes_pending') return { label: 'Local changes pending push', bg: '#fff4db', fg: '#8a5a00' };
    if (syncState === 'synced_to_wrike') return { label: 'Synced with Wrike', bg: '#e7f7ee', fg: '#1f7a44' };
    if (syncState === 'imported_from_wrike') return { label: 'Imported from Wrike', bg: '#e8f1ff', fg: '#1e4e8c' };
    if (syncState === 'sync_failed') return { label: 'Sync failed', bg: '#ffe9e9', fg: '#9a2b2b' };
    if (syncState === 'orphaned_from_wrike') return { label: 'Orphaned from Wrike', bg: '#ffe8cc', fg: '#7c3e00' };
    return { label: 'Local only', bg: '#f1f3f5', fg: '#495057' };
  };

  const selectedCreateSpace = spaceById.get(String(createSpaceId)) || null;
  const selectedCreateSpaceAllowsWrikePush = Boolean(selectedCreateSpace?.settings?.allowSupplyRequestWrikePush);
  const hasItemsInAllWithCurrentFilters = Number(filteredCounts?.all || 0) > 0;
  const showSwitchToAllHint = filteredTasks.length === 0 && activeTab !== 'all' && hasItemsInAllWithCurrentFilters;

  const renderSpaceName = (spaceId) => spaceById.get(String(spaceId))?.name || 'Unknown space';

  const toggleSelected = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((entry) => entry !== id) : [...prev, id]));
  };

  const selectAllVisible = () => {
    setSelectedIds(filteredTasks.map((task) => task.id));
  };

  const clearSelection = () => {
    setSelectedIds([]);
  };

  const handleBulkStatus = async () => {
    if (selectedIds.length === 0) return;
    setBulkSaving(true);
    setBulkFeedback(null);
    try {
      const res = await adminBulkUpdateSupplyRequestsStatus(selectedIds, bulkStatus, '', true);
      const wrikePush = res?.data?.wrikePush || null;

      if (wrikePush?.requested) {
        const pushed = Number(wrikePush.pushed || 0);
        const failed = Number(wrikePush.failed || 0);
        const skipped = Number(wrikePush.skipped || 0);
        const parts = [`Wrike push results: ${pushed} pushed`];
        if (failed > 0) parts.push(`${failed} failed`);
        if (skipped > 0) parts.push(`${skipped} skipped`);

        setBulkFeedback({
          kind: failed > 0 ? 'warning' : 'success',
          text: parts.join(', '),
          details: Array.isArray(wrikePush.errorMessages) ? wrikePush.errorMessages.slice(0, 5) : [],
        });
      } else {
        setBulkFeedback({
          kind: 'success',
          text: 'Selected requests updated.',
          details: [],
        });
      }

      clearSelection();
      await loadRequests({ isRefresh: true });
    } catch (err) {
      setBulkFeedback({
        kind: 'error',
        text: err?.response?.data?.message || 'Failed to update selected requests',
        details: [],
      });
    } finally {
      setBulkSaving(false);
    }
  };

  const handleBulkMove = async () => {
    if (selectedIds.length === 0 || !bulkMoveSpaceId) return;
    setBulkSaving(true);
    try {
      await adminBulkMoveSupplyRequests(selectedIds, bulkMoveSpaceId);
      clearSelection();
      await loadRequests({ isRefresh: true });
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to move selected requests');
    } finally {
      setBulkSaving(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} selected request(s) locally? Wrike tasks will not be deleted.`)) return;
    setBulkSaving(true);
    try {
      await adminBulkDeleteSupplyRequestsLocal(selectedIds);
      clearSelection();
      await loadRequests({ isRefresh: true });
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to delete selected requests');
    } finally {
      setBulkSaving(false);
    }
  };

  return (
    <div className="wrike-page">
      <div className="wrike-header">
        <div>
          <h1 className="wrike-title">Wrike Supply Requests</h1>
          <p className="wrike-subtitle">
            Local-first supply requests tracked in ComSapp with optional Wrike sync.
          </p>
        </div>
        <div className="wrike-header-actions">
          {isAdmin && (
            <label className="wrike-admin-toggle" title="Admin view across all spaces">
              <input
                type="checkbox"
                checked={adminViewAll}
                onChange={(e) => setAdminViewAll(e.target.checked)}
              />
              Admin: All Spaces
            </label>
          )}
          <button className="btn btn-ghost btn-sm" onClick={handlePullSync} disabled={syncing || loading}>
            {syncing ? 'Pulling…' : 'Pull from Wrike'}
          </button>
          {isAdmin && adminViewAll ? (
            <button
              className="btn btn-ghost btn-sm"
              onClick={handleAdminCleanupMirroredTasks}
              disabled={cleaningLegacyTasks || loading}
              title="Remove legacy task-board copies of Wrike-pulled supply requests"
            >
              {cleaningLegacyTasks ? 'Cleaning…' : 'Cleanup Legacy Board Tasks'}
            </button>
          ) : null}
          <button className="btn btn-ghost btn-sm" onClick={() => loadRequests({ isRefresh: true })} disabled={refreshing || loading}>
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => {
              if (!showForm) resetForm();
              setShowForm((value) => !value);
            }}
          >
            {showForm ? 'Close Form' : '+ New Request'}
          </button>
        </div>
      </div>

      {showForm && (
        <div className="wrike-form-card">
          <h2 className="wrike-section-title">Create Supply Request</h2>
          <form className="wrike-form" onSubmit={handleCreate}>
            <div className="wrike-form-grid">
              <div className="form-group wrike-form-span-2">
                <label className="form-label">Space (for local task)</label>
                <select
                  className="form-select"
                  value={createSpaceId}
                  onChange={(e) => setCreateSpaceId(e.target.value)}
                >
                  {spaces.length === 0 ? (
                    <option value="">No spaces available</option>
                  ) : (
                    spaces.map((space) => (
                      <option key={space._id} value={space._id}>
                        {space.name}
                      </option>
                    ))
                  )}
                </select>
                {!selectedCreateSpace?.settings?.allowSupplyRequestWrikePush ? (
                  <p className="panel-muted" style={{ marginTop: 8 }}>
                    Wrike push is disabled for this space. Requests will stay local unless the space setting is enabled.
                  </p>
                ) : null}
              </div>
              <div className="form-group wrike-form-span-2">
                <label className="form-label">What item(s) is needed? *</label>
                <input
                  className="form-input"
                  value={form.itemsNeeded}
                  onChange={(e) => setForm((prev) => ({ ...prev, itemsNeeded: e.target.value }))}
                  placeholder="Example: paper cups, napkins, serving trays"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Department</label>
                <input
                  className="form-input"
                  value={form.department}
                  onChange={(e) => setForm((prev) => ({ ...prev, department: e.target.value }))}
                  placeholder="Department"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Event Name</label>
                <input
                  className="form-input"
                  value={form.eventName}
                  onChange={(e) => setForm((prev) => ({ ...prev, eventName: e.target.value }))}
                  placeholder="Event name"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Required-by Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={form.requiredByDate}
                  onChange={(e) => setForm((prev) => ({ ...prev, requiredByDate: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Website Link</label>
                <input
                  className="form-input"
                  value={form.websiteLink}
                  onChange={(e) => setForm((prev) => ({ ...prev, websiteLink: e.target.value }))}
                  placeholder="https://"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Total Cost Including HST</label>
                <input
                  className="form-input"
                  value={form.totalCostIncludingHst}
                  onChange={(e) => setForm((prev) => ({ ...prev, totalCostIncludingHst: e.target.value }))}
                  placeholder="$0.00"
                />
              </div>
              <div className="form-group wrike-form-span-2">
                <label className="form-label">Quote Notes / 3 Quotes if Over $1000</label>
                <textarea
                  className="form-input wrike-textarea"
                  value={form.quoteNotes}
                  onChange={(e) => setForm((prev) => ({ ...prev, quoteNotes: e.target.value }))}
                  placeholder="Vendor notes, quote summary, or three-quote justification"
                />
              </div>
              <div className="form-group wrike-form-span-2">
                <label className="form-label">Additional Notes</label>
                <textarea
                  className="form-input wrike-textarea"
                  value={form.notes}
                  onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                  placeholder="Optional details to include in the Wrike task"
                />
              </div>
              <div className="form-group wrike-form-span-2">
                <label className="form-label">Local Note</label>
                <textarea
                  className="form-input wrike-textarea"
                  value={form.localNoteText}
                  onChange={(e) => setForm((prev) => ({ ...prev, localNoteText: e.target.value }))}
                  placeholder="Saved only on this request and preserved through Wrike pulls"
                />
              </div>
              <div className="form-group wrike-form-span-2">
                <div className="wrike-file-upload-header">
                  <label className="form-label">Files for this internal request</label>
                  <button type="button" className="btn btn-ghost btn-xs" onClick={() => document.getElementById('supply-request-files')?.click()}>
                    Add Files
                  </button>
                </div>
                <input
                  id="supply-request-files"
                  type="file"
                  multiple
                  className="wrike-hidden-file-input"
                  onChange={handleFormFilesChange}
                />
                <p className="panel-muted" style={{ marginTop: 6 }}>
                  These files will be attached to the internal task and stored with the request record.
                </p>
                {formFiles.length > 0 ? (
                  <div className="wrike-file-list" style={{ marginTop: 10 }}>
                    {formFiles.map((file, index) => (
                      <div className="wrike-file-chip" key={`${file.name}-${file.size}-${file.lastModified}-${index}`}>
                        <div className="wrike-file-main">
                          <span className="wrike-file-name" title={file.name}>{file.name}</span>
                          <span className="wrike-file-meta">{formatBytes(file.size) || file.type || 'Selected file'}</span>
                        </div>
                        <div className="wrike-file-actions">
                          <button type="button" className="btn btn-ghost btn-xs" onClick={() => removeFormFile(index)}>
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
              <div className="form-group wrike-form-span-2">
                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={pushToWrike}
                    disabled={!selectedCreateSpaceAllowsWrikePush}
                    onChange={(e) => setPushToWrike(e.target.checked)}
                  />
                  Push to Wrike after local create
                </label>
              </div>
            </div>

            {formError && <p className="form-error">{formError}</p>}

            <div className="form-row">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Submitting…' : pushToWrike ? 'Create + Push to Wrike' : 'Create Local Request'}
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="wrike-tab-grid">
        {TAB_KEYS.map((key) => (
          <button
            key={key}
            className={key === activeTab ? 'wrike-tab-card active' : 'wrike-tab-card'}
            onClick={() => setActiveTab(key)}
          >
            <span className="wrike-tab-label">{TAB_LABELS[key]}</span>
            <span className="wrike-tab-count">{filteredCounts[key] ?? 0}</span>
          </button>
        ))}
      </div>

      <div className="wrike-filter-bar">
        <select className="form-select" value={filterSpaceId} onChange={(e) => setFilterSpaceId(e.target.value)}>
          <option value="">All spaces</option>
          {spaces.map((space) => (
            <option key={space._id} value={space._id}>
              {space.name}
            </option>
          ))}
        </select>
        <input
          className="form-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by item or title"
        />
        <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          {availableStatuses.map((status) => (
            <option key={status} value={status}>{status}</option>
          ))}
        </select>
        <select className="form-select" value={departmentFilter} onChange={(e) => setDepartmentFilter(e.target.value)}>
          <option value="">All departments</option>
          {availableDepartments.map((department) => (
            <option key={department} value={department}>{department}</option>
          ))}
        </select>
        <select className="form-select" value={syncStateFilter} onChange={(e) => setSyncStateFilter(e.target.value)}>
          <option value="">All sync states</option>
          <option value="local_only">Local only</option>
          <option value="local_changes_pending">Local changes pending push</option>
          <option value="synced_to_wrike">Synced to Wrike</option>
          <option value="imported_from_wrike">Imported from Wrike</option>
          <option value="orphaned_from_wrike">Orphaned from Wrike</option>
          <option value="sync_failed">Sync failed</option>
        </select>
        <input
          type="date"
          className="form-input"
          value={requiredByFilter}
          onChange={(e) => setRequiredByFilter(e.target.value)}
        />
        <select className="form-select" value={attachmentFilter} onChange={(e) => setAttachmentFilter(e.target.value)}>
          <option value="">All attachment states</option>
          <option value="with">Has attachments</option>
          <option value="without">No attachments</option>
        </select>
      </div>

      {error && (
        <div className="wrike-error-banner">
          <div>{error.message}</div>
          {error.diagnostics ? (
            <pre className="wrike-error-diagnostics">{JSON.stringify(error.diagnostics, null, 2)}</pre>
          ) : null}
        </div>
      )}

      {isAdmin && adminViewAll && !loading && filteredTasks.length > 0 && (
        <div className="wrike-bulk-toolbar">
          <span className="wrike-bulk-count">{selectedIds.length} selected</span>
          <button type="button" className="btn btn-ghost btn-sm" onClick={selectAllVisible} disabled={bulkSaving}>
            Select Visible
          </button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={clearSelection} disabled={bulkSaving || selectedIds.length === 0}>
            Clear
          </button>
          <select className="form-select" value={bulkStatus} onChange={(e) => setBulkStatus(e.target.value)} disabled={bulkSaving}>
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <button type="button" className="btn btn-ghost btn-sm" onClick={handleBulkStatus} disabled={bulkSaving || selectedIds.length === 0}>
            {bulkSaving ? 'Working…' : 'Set Status'}
          </button>
          <select className="form-select" value={bulkMoveSpaceId} onChange={(e) => setBulkMoveSpaceId(e.target.value)} disabled={bulkSaving}>
            <option value="">Move to space...</option>
            {spaces.map((space) => (
              <option key={space._id} value={space._id}>{space.name}</option>
            ))}
          </select>
          <button type="button" className="btn btn-ghost btn-sm" onClick={handleBulkMove} disabled={bulkSaving || selectedIds.length === 0 || !bulkMoveSpaceId}>
            Move
          </button>
          <button type="button" className="btn btn-ghost btn-sm wrike-bulk-delete" onClick={handleBulkDelete} disabled={bulkSaving || selectedIds.length === 0}>
            Delete Local
          </button>
        </div>
      )}

      {bulkFeedback ? (
        <div className={`wrike-bulk-feedback ${bulkFeedback.kind}`}>
          <div className="wrike-bulk-feedback-text">{bulkFeedback.text}</div>
          {bulkFeedback.details?.length > 0 ? (
            <ul className="wrike-bulk-feedback-list">
              {bulkFeedback.details.map((detail, index) => (
                <li key={`${detail}-${index}`}>{detail}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      {loading ? (
        <div className="wrike-empty-state">Loading supply requests...</div>
      ) : filteredTasks.length === 0 ? (
        <div className="wrike-empty-state">
          {showSwitchToAllHint
            ? `No items in ${TAB_LABELS[activeTab]} for current filters. ${filteredCounts.all} matching item(s) are available in All.`
            : 'No supply requests match the current filters.'}
        </div>
      ) : (
        <div className="wrike-request-list">
          {filteredTasks.map((task) => {
            const selectedStatus = statusDraft[task.id] ?? task.standardStatus ?? 'other';
            const showCustomEditor = selectedStatus === 'other';
            const isEditing = editingId === task.id;
            const syncInfo = syncUi(task.syncState);
            const canPushToWrike = !task.wrikeTaskId || task.syncState === 'local_changes_pending' || task.syncState === 'sync_failed';
            const taskSpace = spaceById.get(String(task.spaceId));
            const allowWrikePushForTask = Boolean(taskSpace?.settings?.allowSupplyRequestWrikePush);
            const pushButtonLabel = !task.wrikeTaskId ? 'Push to Wrike' : 'Push Updates';
            const pushBlockedLabel = 'Wrike push disabled by space policy';
            const isSelected = selectedIds.includes(task.id);
            const taskPermalink = toHttpUrl(task.permalink);
            const websiteHref = toHttpUrl(task.parsedFields?.websiteLink);
            const importedFiles = Array.isArray(task.importedFiles) ? task.importedFiles : [];
            const wrikeComments = Array.isArray(task.wrikeComments) ? task.wrikeComments : [];
            const localNotes = Array.isArray(task.localNotes) ? task.localNotes : [];
            const quoteNotesText = String(task.parsedFields?.quoteNotes || '');
            const linkedQuoteFiles = importedFiles.filter((file) => {
              const fileName = String(file?.name || '').trim();
              if (!fileName || !quoteNotesText) return false;
              return quoteNotesText.toLowerCase().includes(fileName.toLowerCase());
            });

            return (
            <article className={`wrike-request-card ${isSelected ? 'selected' : ''}`} key={task.id}>
              <div className="wrike-request-top">
                <div>
                  {isAdmin && adminViewAll && (
                    <label className="wrike-select-row">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelected(task.id)}
                      />
                      Select
                    </label>
                  )}
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div className="wrike-status-pill">{task.visibleStatus || 'Unknown'}</div>
                    <span
                      style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        borderRadius: '999px',
                        padding: '4px 10px',
                        background: syncInfo.bg,
                        color: syncInfo.fg,
                      }}
                    >
                      {syncInfo.label}
                    </span>
                  </div>
                  <div style={{ marginTop: '8px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '12px', fontWeight: 600, borderRadius: '999px', padding: '4px 10px', background: '#eef2ff', color: '#334155' }}>
                      {renderSpaceName(task.spaceId)}
                    </span>
                  </div>
                  <div className="wrike-status-editor" style={{ marginTop: '8px', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <select
                      className="form-select"
                      value={selectedStatus}
                      disabled={statusSavingId === task.id}
                      onChange={(e) => handleStatusSelect(task, e.target.value)}
                    >
                      {STATUS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {showCustomEditor && (
                      <input
                        className="form-input"
                        style={{ minWidth: '180px' }}
                        placeholder="Custom status label"
                        value={customStatusDraft[task.id] ?? (task.standardStatus === 'other' ? task.visibleStatus : '')}
                        onChange={(e) =>
                          setCustomStatusDraft((prev) => ({
                            ...prev,
                            [task.id]: e.target.value,
                          }))
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleCustomStatusSave(task);
                          }
                        }}
                        disabled={statusSavingId === task.id}
                      />
                    )}
                    {showCustomEditor && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => handleCustomStatusSave(task)}
                        disabled={statusSavingId === task.id || !String(customStatusDraft[task.id] || '').trim()}
                      >
                        {statusSavingId === task.id ? 'Saving…' : 'Save'}
                      </button>
                    )}
                  </div>
                  <h3 className="wrike-request-title">{task.title}</h3>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => (isEditing ? cancelEdit() : startEdit(task))}>
                    {isEditing ? 'Cancel Edit' : 'Edit Local Copy'}
                  </button>
                  {isAdmin ? (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => handleDeleteLocalCopy(task)}
                      disabled={deletingId === task.id}
                      title="Delete only from ComSapp local store"
                    >
                      {deletingId === task.id ? 'Deleting…' : 'Delete Local Copy'}
                    </button>
                  ) : null}
                  {canPushToWrike && allowWrikePushForTask ? (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => pushLocalToWrike(task)}
                      disabled={pushSavingId === task.id}
                    >
                      {pushSavingId === task.id ? 'Pushing…' : pushButtonLabel}
                    </button>
                  ) : !allowWrikePushForTask ? (
                    <span style={{ fontSize: '12px', color: '#8a5a00', fontWeight: 600 }}>{pushBlockedLabel}</span>
                  ) : (
                    <span style={{ fontSize: '12px', color: '#2f6d3e', fontWeight: 600 }}>Wrike linked</span>
                  )}
                  {taskPermalink ? (
                    <a className="wrike-open-link" href={taskPermalink} target="_blank" rel="noreferrer">
                      Open in Wrike
                    </a>
                  ) : null}
                </div>
              </div>

              <p className="wrike-description-preview">{task.descriptionPreview || 'No description preview available.'}</p>

              <div className="wrike-meta-grid">
                <div>
                  <span className="wrike-meta-label">Sync</span>
                  <span className="wrike-meta-value">{task.syncState || 'local_only'}</span>
                </div>
                <div>
                  <span className="wrike-meta-label">Source</span>
                  <span className="wrike-meta-value">{task.source || 'local'}</span>
                </div>
                <div>
                  <span className="wrike-meta-label">Creator</span>
                  <span className="wrike-meta-value">{joinPeople(task.creator)}</span>
                </div>
                <div>
                  <span className="wrike-meta-label">Assignees</span>
                  <span className="wrike-meta-value">{joinPeople(task.assignees)}</span>
                </div>
                <div>
                  <span className="wrike-meta-label">Created</span>
                  <span className="wrike-meta-value">{formatDateTime(task.createdAt)}</span>
                </div>
                <div>
                  <span className="wrike-meta-label">Updated</span>
                  <span className="wrike-meta-value">{formatDateTime(task.updatedAt)}</span>
                </div>
                <div>
                  <span className="wrike-meta-label">Completed</span>
                  <span className="wrike-meta-value">{formatDateTime(task.completedAt)}</span>
                </div>
                <div>
                  <span className="wrike-meta-label">Due / Required by</span>
                  <span className="wrike-meta-value">{task.dueDate || task.parsedFields?.requiredByDate || '—'}</span>
                </div>
              </div>

              <div className="wrike-field-grid">
                <div>
                  <span className="wrike-meta-label">Item(s) Needed</span>
                  <span className="wrike-meta-value">{task.parsedFields?.itemsNeeded || '—'}</span>
                </div>
                <div>
                  <span className="wrike-meta-label">Department</span>
                  <span className="wrike-meta-value">{task.parsedFields?.department || '—'}</span>
                </div>
                <div>
                  <span className="wrike-meta-label">Event Name</span>
                  <span className="wrike-meta-value">{task.parsedFields?.eventName || '—'}</span>
                </div>
                <div>
                  <span className="wrike-meta-label">Website Link</span>
                  {websiteHref ? (
                    <a className="wrike-inline-link" href={websiteHref} target="_blank" rel="noreferrer" title={task.parsedFields.websiteLink}>
                      {urlDisplayLabel(task.parsedFields.websiteLink)}
                    </a>
                  ) : task.parsedFields?.websiteLink ? (
                    <span className="wrike-meta-value">{task.parsedFields.websiteLink}</span>
                  ) : (
                    <span className="wrike-meta-value">—</span>
                  )}
                </div>
                <div>
                  <span className="wrike-meta-label">Total Cost incl. HST</span>
                  <span className="wrike-meta-value">{task.parsedFields?.totalCostIncludingHst || '—'}</span>
                </div>
                <div>
                  <span className="wrike-meta-label">Attachments</span>
                  <span className="wrike-meta-value">{task.attachmentLabel}</span>
                  {importedFiles.length > 0 && (
                    <div className="wrike-file-list">
                      {importedFiles.map((file) => (
                        <div className="wrike-file-chip" key={file.id || file.wrikeAttachmentId || file.name}>
                          <div className="wrike-file-main">
                            <span className="wrike-file-name" title={file.name}>{file.name}</span>
                            <span className="wrike-file-meta">{formatBytes(file.size) || file.mimeType || 'Imported file'}</span>
                          </div>
                          <div className="wrike-file-actions">
                            <button type="button" className="btn btn-ghost btn-xs" onClick={() => handleOpenImportedFile(file)}>
                              Open
                            </button>
                            <button type="button" className="btn btn-ghost btn-xs" onClick={() => handleDownloadImportedFile(file)}>
                              Download
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="wrike-notes-block">
                <span className="wrike-meta-label">Quote Notes / 3 Quotes</span>
                <p className="wrike-notes-value">{quoteNotesText || 'None provided.'}</p>
                {linkedQuoteFiles.length > 0 && (
                  <div className="wrike-file-list" style={{ marginTop: '8px' }}>
                    {linkedQuoteFiles.map((file) => (
                      <div className="wrike-file-chip" key={`quote-${file.id || file.name}`}>
                        <div className="wrike-file-main">
                          <span className="wrike-file-name" title={file.name}>{file.name}</span>
                          <span className="wrike-file-meta">{formatBytes(file.size) || file.mimeType || 'Linked file'}</span>
                        </div>
                        <div className="wrike-file-actions">
                          <button type="button" className="btn btn-ghost btn-xs" onClick={() => handleOpenImportedFile(file)}>
                            Open
                          </button>
                          <button type="button" className="btn btn-ghost btn-xs" onClick={() => handleDownloadImportedFile(file)}>
                            Download
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="wrike-notes-block">
                <span className="wrike-meta-label">Local Notes</span>
                {localNotes.length > 0 ? (
                  <div className="wrike-comment-list" style={{ marginTop: 8 }}>
                    {localNotes.map((note, index) => (
                      <div className="wrike-comment-item" key={note.id || `${task.id}-local-note-${index}`}>
                        <div className="wrike-comment-meta">
                          <span className="wrike-comment-author" title={note.authorEmail || note.authorId || ''}>
                            {note.authorName || 'ComSapp user'}
                          </span>
                          <span>{formatDateTime(note.createdAt)}</span>
                        </div>
                        <p className="wrike-comment-text">{note.text || 'No note text.'}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="wrike-notes-value">No local notes yet.</p>
                )}
                <div style={{ marginTop: 10 }}>
                  <textarea
                    className="form-input wrike-textarea"
                    style={{ width: '100%' }}
                    placeholder="Add a local note that stays with this request"
                    value={localNoteDrafts[task.id] || ''}
                    onChange={(e) => setLocalNoteDrafts((prev) => ({ ...prev, [task.id]: e.target.value }))}
                  />
                  <div className="form-row" style={{ marginTop: 8 }}>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => handleAddLocalNote(task)}
                      disabled={localNoteSavingId === task.id || !String(localNoteDrafts[task.id] || '').trim()}
                    >
                      {localNoteSavingId === task.id ? 'Saving…' : 'Add Local Note'}
                    </button>
                  </div>
                </div>
              </div>

              {wrikeComments.length > 0 && (
                <div className="wrike-comments-block">
                  <div className="wrike-comments-header">
                    <span className="wrike-meta-label">Wrike Messages</span>
                    <span className="wrike-comment-count">{wrikeComments.length}</span>
                  </div>
                  <div className="wrike-comment-list">
                    {wrikeComments.map((comment, index) => (
                      <div className="wrike-comment-item" key={comment.id || `${task.id}-comment-${index}`}>
                        <div className="wrike-comment-meta">
                          <span className="wrike-comment-author" title={comment.authorEmail || comment.authorId || ''}>
                            {comment.authorName || 'Wrike user'}
                          </span>
                          <span>{formatDateTime(comment.createdAt)}</span>
                        </div>
                        <p className="wrike-comment-text">{comment.text || 'No message text.'}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {isEditing && editDraft && (
                <div className="wrike-form-card" style={{ marginTop: '12px' }}>
                  <h4 className="wrike-section-title">Edit Local Copy</h4>
                  <div className="wrike-form-grid">
                    <div className="form-group wrike-form-span-2">
                      <label className="form-label">Item(s) Needed</label>
                      <input
                        className="form-input"
                        value={editDraft.itemsNeeded}
                        onChange={(e) => handleEditField('itemsNeeded', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Department</label>
                      <input className="form-input" value={editDraft.department} onChange={(e) => handleEditField('department', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Event Name</label>
                      <input className="form-input" value={editDraft.eventName} onChange={(e) => handleEditField('eventName', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Required-by Date</label>
                      <input type="date" className="form-input" value={editDraft.requiredByDate} onChange={(e) => handleEditField('requiredByDate', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Website Link</label>
                      <input className="form-input" value={editDraft.websiteLink} onChange={(e) => handleEditField('websiteLink', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Total Cost incl. HST</label>
                      <input className="form-input" value={editDraft.totalCostIncludingHst} onChange={(e) => handleEditField('totalCostIncludingHst', e.target.value)} />
                    </div>
                    <div className="form-group wrike-form-span-2">
                      <label className="form-label">Quote Notes</label>
                      <textarea className="form-input wrike-textarea" value={editDraft.quoteNotes} onChange={(e) => handleEditField('quoteNotes', e.target.value)} />
                    </div>
                    <div className="form-group wrike-form-span-2">
                      <label className="form-label">Additional Notes</label>
                      <textarea className="form-input wrike-textarea" value={editDraft.notes} onChange={(e) => handleEditField('notes', e.target.value)} />
                    </div>
                    <div className="form-group wrike-form-span-2">
                      <label className="form-label">Space</label>
                      <select
                        className="form-select"
                        value={editDraft.spaceId || ''}
                        onChange={(e) => handleEditField('spaceId', e.target.value)}
                      >
                        {spaces.length === 0 ? (
                          <option value="">No spaces available</option>
                        ) : (
                          spaces.map((space) => (
                            <option key={space._id} value={space._id}>
                              {space.name}
                            </option>
                          ))
                        )}
                      </select>
                    </div>
                  </div>
                  <div className="form-row">
                    <button type="button" className="btn btn-primary" onClick={() => saveLocalEdits(task)} disabled={editSavingId === task.id}>
                      {editSavingId === task.id ? 'Saving…' : 'Save Local Changes'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={() => saveLocalEdits(task, { pushAfterSave: true })}
                      disabled={pushSavingId === task.id}
                    >
                      {pushSavingId === task.id ? 'Saving + Pushing…' : task.wrikeTaskId ? 'Save + Push Updates' : 'Save + Push to Wrike'}
                    </button>
                    <button type="button" className="btn btn-ghost" onClick={cancelEdit}>Cancel</button>
                  </div>
                </div>
              )}
            </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
